/* eslint-disable no-return-assign */
import getCategoricalChartColors from '../../Persona/Utils/getCategoricalChartColors';

// Lickly gets the on-brand DS categorical palette; other brands keep this colourful array.
// CI charts are Highcharts (SVG) and inline-style backgrounds, so the var() tokens resolve.
// AAPU-6711
export default function getColorfulArray() {
  return getCategoricalChartColors([
    '#FFD600',
    '#7C3AED',
    '#EC4899',
    '#34D35C',
    '#3B82F6',
    '#FF7A1A',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#06B6D4',
    '#84CC16',
    '#F97316',
    '#6366F1',
    '#14B8A6',
    '#A855F7',
    '#E11D48',
    '#DB2777',
    '#F43F5E',
    '#0EA5E9',
    '#22D3EE',
    '#8B4513',
    '#FFB347',
    '#7FFF00',
    '#FF69B4',
    '#00CED1',
    '#9400D3',
    '#FF4500',
    '#00FA9A',
    '#B22222',
  ]);
}

export function getOptionAScaleColor() {
  return [
    '#021A3A',
    '#03224A',
    '#042959',
    '#053473',
    '#064699',
    '#0751AC',
    '#0857BF',
    '#0968E6',
    '#0A74FF',
    '#3A90FF',
    '#6AA8FF',
    '#8EBEFF',
    '#A3CCFF',
    '#B3D4FF',
    '#D6E7FF',
  ];
}

function hexToHSL(hex) {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h, s, l) {
  const normalizedS = s / 100;
  const normalizedL = l / 100;
  const k = n => (n + h / 30) % 12;
  const a = normalizedS * Math.min(normalizedL, 1 - normalizedL);
  const f = n => {
    const x =
      normalizedL - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(x * 255);
  };
  return `#${(16777216 + f(0) * 65536 + f(8) * 256 + f(4))
    .toString(16)
    .slice(1)}`;
}

// Resolve a CSS var() token (e.g. the Lickly chart palette) to its computed hex, since the
// HSL scale math below needs a concrete hex. Plain hex values pass through unchanged. AAPU-6711
export function resolveToHex(color) {
  if (typeof color !== 'string' || !color.startsWith('var(')) return color;
  const tokenName = color.slice(4, color.indexOf(')')).split(',')[0].trim();
  const resolved =
    typeof document !== 'undefined'
      ? getComputedStyle(document.documentElement)
          .getPropertyValue(tokenName)
          .trim()
      : '';
  return resolved || '#CE3887';
}

export function getColorScale(baseIndex) {
  const baseColor = resolveToHex(
    getColorfulArray()[baseIndex % getColorfulArray().length]
  );
  const { h, s, l } = hexToHSL(baseColor);

  // AAPU-6711: Lickly ramps the Share of Voice gradient light → dark; other brands keep the
  // original dark → light order.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';

  const steps = 10;
  const scale = [];
  // Lickly: spread the steps evenly across a fixed lightness band (light → dark) rather than
  // offsetting the base ±40 and clamping. Clamping flattened multiple steps to 100/0 for light
  // base tints, which produced a pure-white slice and repeated colours; a fixed band keeps all
  // 10 steps distinct and inside a safe range. The floor is kept at 52% (a clear mid-tone) so no
  // step reads dark/black, and the ceiling at 88% so none reads pure white. AAPU-6711
  const LICKLY_MAX_L = 88;
  const LICKLY_MIN_L = 52;
  for (let i = 0; i < steps; i += 1) {
    const lum = isLickly
      ? LICKLY_MAX_L - (i * (LICKLY_MAX_L - LICKLY_MIN_L)) / (steps - 1)
      : Math.max(0, Math.min(100, l - 40 + (i * 80) / (steps - 1)));
    scale.push(hslToHex(h, s, lum));
  }

  return scale;
}

export function getOptionBScaleColor() {
  return [
    '#3B170D',
    '#441A0F',
    '#522213',
    '#6A2C19',
    '#7C3220',
    '#8D3A21',
    '#A54227',
    '#B04929',
    '#C95730',
    '#EB6137',
    '#F27A52',
    '#F5936F',
    '#F8AD8B',
    '#F9C4A8',
    '#F5D8C9',
  ];
}

export function getColorBySentiment(sentiment) {
  // AAPU-6711: Lickly maps sentiment to the DS green/champagne/danger tokens (root variables);
  // other brands keep the original colours. Consumers render in Highcharts (SVG), so var() resolves.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';

  const colors = isLickly
    ? {
        'Very Positive': 'var(--ds-green-600)',
        Positive: 'var(--ds-green-400)',
        Neutral: 'var(--ds-champagne-500)',
        Negative: 'var(--ds-danger-500)',
        'Very Negative': 'var(--ds-danger-700)',
      }
    : {
        'Very Positive': '#B2DA60',
        Positive: '#C6F26B',
        Neutral: '#FFBB28',
        Negative: '#EB6137',
        'Very Negative': '#BC4E2C',
      };
  // If no sentiment provided, return the entire colors object
  if (sentiment === undefined) {
    return colors;
  }
  return colors[sentiment];
}

export function getColorByEmotionName(emotion) {
  // AAPU-6711: Lickly maps each emotion to a distinct DS hue token (root variables); other
  // brands keep the originals. Consumers render in Highcharts (SVG), so var() resolves.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';

  const base = isLickly
    ? {
        Joy: 'var(--ds-champagne-500)',
        Neutral: 'var(--ds-neutral-400)',
        Surprise: 'var(--ds-pink-500)',
        Sadness: 'var(--ds-blue-500)',
        Fear: 'var(--ds-info-700)',
        Anger: 'var(--ds-danger-500)',
        Disgust: 'var(--ds-green-600)',
      }
    : {
        Joy: '#FFD93D',
        Neutral: '#A0A4A8',
        Surprise: '#FF9F1C',
        Sadness: '#4A90E2',
        Fear: '#6C5CE7',
        Anger: '#E63946',
        Disgust: '#2D6A4F',
      };

  const colors = {
    ...base,
    // Handle lowercase versions
    joy: base.Joy,
    neutral: base.Neutral,
    surprise: base.Surprise,
    sadness: base.Sadness,
    fear: base.Fear,
    anger: base.Anger,
    disgust: base.Disgust,
  };
  if (emotion === undefined) {
    return base;
  }
  return (
    colors[emotion] ||
    colors[emotion.charAt(0).toUpperCase() + emotion.slice(1).toLowerCase()]
  );
}

export function getColorByEmotionDistributionValue(value) {
  // AAPU-6711: Lickly uses the DS Lickly Blue (teal) ramp (root variables, light → dark);
  // other brands keep the original blue ramp.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';

  const scale = isLickly
    ? [
        'var(--ds-blue-50)', // 0–14 (lightest)
        'var(--ds-blue-100)', // 15–29
        'var(--ds-blue-200)', // 30–44
        'var(--ds-blue-300)', // 45–59
        'var(--ds-blue-500)', // 60–74
        'var(--ds-blue-700)', // 75–89
        'var(--ds-blue-900)', // 90–100 (darkest)
      ]
    : [
        'var(--brand-tint-100)', // 0–14 (lightest)
        '#0A74FF66', // 15–29
        '#0A74FFB3', // 30–44
        '#0A74FFE5', // 45–59
        '#0857BF', // 60–74
        '#053473', // 75–89
        '#021A33', // 90–100 (darkest)
      ];

  if (value <= 14) return scale[0];
  if (value <= 29) return scale[1];
  if (value <= 44) return scale[2];
  if (value <= 59) return scale[3];
  if (value <= 74) return scale[4];
  if (value <= 89) return scale[5];
  return scale[6]; // 90–100
}

function generateScale(hex) {
  const lighten = (hexColor, factor) => {
    const num = parseInt(hexColor.slice(1), 16);
    const r0 = Math.floor(num / 65536) % 256;
    const g0 = Math.floor(num / 256) % 256;
    const b0 = num % 256;

    const r = Math.min(255, Math.floor(r0 + (255 - r0) * factor));
    const g = Math.min(255, Math.floor(g0 + (255 - g0) * factor));
    const b = Math.min(255, Math.floor(b0 + (255 - b0) * factor));

    const hexString = (16777216 + r * 65536 + g * 256 + b)
      .toString(16)
      .slice(1)
      .toUpperCase();
    return `#${hexString}`;
  };

  return [
    lighten(hex, 0.85),
    lighten(hex, 0.65),
    lighten(hex, 0.45),
    lighten(hex, 0.25),
    lighten(hex, 0.1),
    lighten(hex, 0.05),
    hex,
  ];
}

// AAPU-6711: brand-aware emotion base colours. generateScale() does hex-lighten math, so these
// must be concrete hex; on Lickly they are the DS token hexes (mirrors getColorByEmotionName).
// Computed per call so data-tenant is read at render time.
function getEmotionBaseColors() {
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';
  return isLickly
    ? {
        neutral: '#A89999', // --ds-neutral-400
        surprise: '#CE3887', // --ds-pink-500
        joy: '#B2832F', // --ds-champagne-500
        sadness: '#3B9EA9', // --ds-blue-500
        fear: '#255598', // --ds-info-700
        anger: '#E0493F', // --ds-danger-500
        disgust: '#006E78', // --ds-blue-700 (was --ds-green-600; AAPU-6776 no-green, kept distinct from sadness blue-500)
      }
    : {
        neutral: '#A0A4A8',
        surprise: '#FF9F1C',
        joy: '#FFD93D',
        sadness: '#4A90E2',
        fear: '#6C5CE7',
        anger: '#E63946',
        disgust: '#2D6A4F',
      };
}

function getEmotionScales() {
  const base = getEmotionBaseColors();
  const scales = {};
  Object.keys(base).forEach(emotion => {
    scales[emotion] = generateScale(base[emotion]);
  });
  return scales;
}

export function getColorByEmotionScale(emotion, value) {
  if (!emotion) return '#CCCCCC';

  const key = emotion.toLowerCase();
  const scale = getEmotionScales()[key];

  if (!scale) return '#CCCCCC';

  if (value <= 14) return scale[0];
  if (value <= 29) return scale[1];
  if (value <= 44) return scale[2];
  if (value <= 59) return scale[3];
  if (value <= 74) return scale[4];
  if (value <= 89) return scale[5];
  return scale[6];
}

export function getColorBySentimentScale(emotion, value) {
  const clampedValue = Math.max(0, Math.min(100, value));

  const pickColor = scale => {
    const index = Math.floor((clampedValue / 100) * (scale.length - 1));
    return scale[index];
  };

  // AAPU-6711: Lickly maps the sentiment ramps to the DS danger / champagne / green scales
  // (light → dark); other brands keep the original ramps.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';

  // Lickly items are full var() tokens (returned as-is); other brands keep bare hex (prefixed
  // with '#' below).
  const scales = isLickly
    ? {
        negative: ['var(--ds-danger-50)', 'var(--ds-danger-100)', 'var(--ds-danger-300)', 'var(--ds-danger-500)', 'var(--ds-danger-700)', 'var(--ds-danger-900)'],
        verynegative: ['var(--ds-danger-50)', 'var(--ds-danger-100)', 'var(--ds-danger-300)', 'var(--ds-danger-500)', 'var(--ds-danger-700)', 'var(--ds-danger-900)'],
        neutral: ['var(--ds-champagne-50)', 'var(--ds-champagne-100)', 'var(--ds-champagne-300)', 'var(--ds-champagne-500)', 'var(--ds-champagne-700)', 'var(--ds-champagne-900)'],
        positive: ['var(--ds-green-50)', 'var(--ds-green-100)', 'var(--ds-green-300)', 'var(--ds-green-500)', 'var(--ds-green-700)', 'var(--ds-green-900)'],
        verypositive: ['var(--ds-green-50)', 'var(--ds-green-100)', 'var(--ds-green-300)', 'var(--ds-green-500)', 'var(--ds-green-700)', 'var(--ds-green-900)'],
      }
    : {
        negative: ['FDEFEB', 'F9CEC1', 'BC4E2C', '6A2C19', '522213', 'EB6137'],
        verynegative: ['FDEFEB', 'F9CEC1', 'BC4E2C', '6A2C19', '522213', 'EB6137'],
        neutral: ['FFF5CC', 'F5CA50', 'E6B942', 'D1A12F', 'B28B1F', '9C741C'],
        positive: ['EDFBD1', 'F6FDE9', 'B2DA60', '9EC256', '779140', '4B6F20'],
        verypositive: ['EDFBD1', 'F6FDE9', 'B2DA60', '9EC256', '779140', '4B6F20'],
      };

  const scale = scales[emotion];

  if (!scale) {
    console.warn(`Unknown emotion: ${emotion}, returning default color`);
    return '#000000';
  }

  const picked = pickColor(scale);
  // Lickly items are already full var() tokens; the legacy hex items need the '#' prefix.
  return isLickly ? picked : `#${picked}`;
}

export function getSentimentBarColor(netSentiment) {
  // AAPU-6711: Lickly maps the sentiment ramps to the DS danger / champagne / green scales
  // (light → dark); other brands keep the original ramps.
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';
  if (netSentiment > 0) return isLickly ? 'var(--ds-green-500)' : '#C6F26B'; // green
  if (netSentiment < 0) return isLickly ? 'var(--ds-danger-500)' : '#EB6137'; // red
  return isLickly ? 'var(--ds-neutral-500)' : '#6B7280'; // gray
}

export function mapProducts(data) {
  if (!data) return [];

  const {
    clientProduct,
    competingProducts = [],
    productLogos = {},
    productDomains = {},
  } = data;

  const allProducts = [clientProduct, ...competingProducts];

  return allProducts.map(name => {
    const domain = productDomains[name];
    // Use Google favicon service (reliable, no token needed)
    const logo = productLogos[name] ||
      (domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null);
    return { name, logo };
  });
}

export function mapProductValues(products, values) {
  if (!Array.isArray(products) || !Array.isArray(values)) {
    throw new Error('Both arguments must be arrays');
  }

  return products.map((product, index) => ({
    ...product,
    value: values[index] ?? null,
  }));
}

export function processDataByInterval(data, categories, products) {
  if (
    !Array.isArray(data) ||
    !Array.isArray(categories) ||
    !Array.isArray(products)
  ) {
    throw new Error('Invalid input: all parameters must be arrays');
  }

  return products.map((product, productIndex) => {
    const productRow = data[productIndex];

    // Handle both object and string product formats
    const productObj =
      typeof product === 'object' && product !== null
        ? product
        : { name: product, logo: null };

    if (!productRow) return { product: productObj, categories: [] };

    const mappedCategories = productRow.map((value, categoryIndex) => ({
      category: categories[categoryIndex],
      value,
    }));

    return {
      product: productObj,
      categories: mappedCategories,
    };
  });
}

/**
 * Transform data structure for table comparison component
 * @param {Array} data - Array of product data with structure: [{ product: {...}, categories: [{ category: "...", value: [...] }] }]
 * @param {Array} categories - Array of category names that map to indices in value arrays (e.g., sentimentCategories)
 * @returns {Array} Transformed data with structure: [{ id: "...", name: "...", categoryName: { key1: value1, key2: value2, ... } }]
 */
export function getDataForTableCompare(data, categories) {
  if (!Array.isArray(data) || !Array.isArray(categories) || data.length === 0) {
    return [];
  }

  // Helper function to convert category name to camelCase key
  const toCamelCase = str => {
    return str
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  };

  // Build category key map dynamically from categories by index
  const categoryKeyMap = categories.reduce(
    (map, category) => ({
      ...map,
      [category]: toCamelCase(category),
    }),
    {}
  );

  return data.map((productData, index) => {
    const product =
      typeof productData.product === 'object'
        ? productData.product
        : { name: productData.product, logo: null };

    // Initialize the result object with id and name
    const result = {
      id: String(index),
      name: product.name,
    };

    // Transform categories
    if (productData.categories && Array.isArray(productData.categories)) {
      productData.categories.forEach(categoryItem => {
        const categoryName = categoryItem.category;
        const valueArray = categoryItem.value;

        // Initialize the category object
        result[categoryName] = {};

        // Map each value in the array to its corresponding category by index
        if (Array.isArray(valueArray) && valueArray.length > 0) {
          valueArray.forEach((value, categoryIndex) => {
            const categoryNameFromIndex = categories[categoryIndex];
            if (categoryNameFromIndex) {
              const categoryKey =
                categoryKeyMap[categoryNameFromIndex] ||
                toCamelCase(categoryNameFromIndex);
              // Convert decimal (0.12) to percentage (12) and round
              result[categoryName][categoryKey] = Math.round(value * 100);
            }
          });
        }
      });
    }

    return result;
  });
}

export function processTimeIntervalData(
  byTimeInterval,
  products,
  timeframe,
  fieldName
) {
  if (
    !byTimeInterval ||
    !Array.isArray(byTimeInterval) ||
    !products ||
    !Array.isArray(products)
  ) {
    return [];
  }

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  if (timeframe === 'oneMonth') {
    const trendsData = byTimeInterval
      .filter(interval => interval.startDate && interval.endDate)
      .map(interval => {
        const start = new Date(interval.startDate);
        const end = new Date(interval.endDate);

        const startMonth = monthNames[start.getMonth()];
        const startDay = start.getDate();
        const endMonth = monthNames[end.getMonth()];
        const endDay = end.getDate();

        const dateLabel =
          startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;

        const intervalValues = mapProductValues(
          products,
          interval[fieldName] || []
        );

        const trendEntry = {
          month: dateLabel,
          startDate: interval.startDate,
        };

        products.forEach(product => {
          const productData = intervalValues.find(p => p.name === product.name);
          trendEntry[product.name] = parseFloat(
            ((productData?.value || 0) * 100).toFixed(2)
          );
        });

        return trendEntry;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .map(({ startDate, ...entry }) => entry);

    return trendsData;
  }

  const monthlyData = {};

  byTimeInterval.forEach(interval => {
    if (!interval.startDate) return;

    const start = new Date(interval.startDate);
    const year = start.getFullYear();
    const month = start.getMonth();

    const monthKey = `${year}-${month}`;
    const monthLabel = monthNames[month];

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        month: monthLabel,
        productValues: {},
        count: 0,
      };
    }

    const intervalValues = mapProductValues(
      products,
      interval[fieldName] || []
    );

    intervalValues.forEach(product => {
      if (!monthlyData[monthKey].productValues[product.name]) {
        monthlyData[monthKey].productValues[product.name] = [];
      }
      monthlyData[monthKey].productValues[product.name].push(
        product.value || 0
      );
    });

    monthlyData[monthKey].count += 1;
  });

  const lastInterval = byTimeInterval[byTimeInterval.length - 1];

  if (lastInterval?.startDate) {
    const d = new Date(lastInterval.startDate);
    const lastKey = `${d.getFullYear()}-${d.getMonth()}`;

    if (!monthlyData[lastKey]) {
      monthlyData[lastKey] = {
        month: monthNames[d.getMonth()],
        productValues: {},
        count: 0,
      };
    }
  }

  const trendsData = Object.keys(monthlyData)
    .sort((a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      return yearA - yearB || monthA - monthB;
    })
    .map(monthKey => {
      const monthData = monthlyData[monthKey];
      const trendEntry = {
        month: monthData.month,
      };

      products.forEach(product => {
        const values = monthData.productValues[product.name] || [];
        const average =
          values.length > 0
            ? values.reduce((sum, val) => sum + val, 0) / values.length
            : 0;
        trendEntry[product.name] = parseFloat((average * 100).toFixed(2));
      });

      return trendEntry;
    });

  return trendsData;
}

export function processTimeIntervalMatrixData(
  byTimeInterval,
  categories,
  timeframe,
  fieldName
) {
  if (
    !byTimeInterval ||
    !Array.isArray(byTimeInterval) ||
    !categories ||
    !Array.isArray(categories)
  ) {
    return [];
  }

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const processedIntervals = byTimeInterval
    .filter(interval => interval.startDate)
    .map(interval => {
      const start = new Date(interval.startDate);
      const matrix = interval[fieldName] || [];

      let dateLabel;
      if (timeframe === 'oneMonth' && interval.endDate) {
        const end = new Date(interval.endDate);
        const startMonth = monthNames[start.getMonth()];
        const startDay = start.getDate();
        const endMonth = monthNames[end.getMonth()];
        const endDay = end.getDate();

        dateLabel =
          startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      } else {
        dateLabel = monthNames[start.getMonth()];
      }

      return {
        dateLabel,
        startDate: interval.startDate,
        monthKey: `${start.getFullYear()}-${start.getMonth()}`,
        matrix: Array.isArray(matrix) ? matrix : [],
      };
    })
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  if (timeframe === 'oneMonth') {
    const categoryDataMap = {};
    categories.forEach(category => {
      categoryDataMap[category] = [];
    });

    processedIntervals.forEach(({ matrix }) => {
      if (matrix.length > 0 && Array.isArray(matrix[0])) {
        // 2D matrix
        categories.forEach((category, categoryIndex) => {
          const values = matrix
            .filter(row => Array.isArray(row))
            .map(row => row[categoryIndex] || 0);

          const average =
            values.length > 0
              ? values.reduce((sum, v) => sum + v, 0) / values.length
              : 0;

          categoryDataMap[category].push(
            parseFloat((average * 100).toFixed(2))
          );
        });
      } else if (matrix.length === categories.length) {
        // 1D array
        categories.forEach((category, categoryIndex) => {
          categoryDataMap[category].push(
            parseFloat(((matrix[categoryIndex] || 0) * 100).toFixed(2))
          );
        });
      }
    });

    return categories.map(category => ({
      name: category,
      data: categoryDataMap[category] || [],
    }));
  }

  const monthlyData = {};

  processedIntervals.forEach(interval => {
    const { monthKey, matrix } = interval;
    const start = new Date(interval.startDate);
    const monthLabel = monthNames[start.getMonth()];

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        month: monthLabel,
        categoryValues: {},
        count: 0,
      };
    }

    if (matrix.length > 0 && Array.isArray(matrix[0])) {
      categories.forEach((category, categoryIndex) => {
        if (!monthlyData[monthKey].categoryValues[category]) {
          monthlyData[monthKey].categoryValues[category] = [];
        }

        const values = matrix
          .filter(row => Array.isArray(row))
          .map(row => row[categoryIndex] || 0);

        const avg =
          values.length > 0
            ? values.reduce((s, v) => s + v, 0) / values.length
            : 0;

        monthlyData[monthKey].categoryValues[category].push(avg);
      });
    } else if (matrix.length === categories.length) {
      categories.forEach((category, categoryIndex) => {
        if (!monthlyData[monthKey].categoryValues[category]) {
          monthlyData[monthKey].categoryValues[category] = [];
        }

        monthlyData[monthKey].categoryValues[category].push(
          matrix[categoryIndex] || 0
        );
      });
    }

    monthlyData[monthKey].count += 1;
  });

  const lastInterval = processedIntervals[processedIntervals.length - 1];
  if (lastInterval) {
    const d = new Date(lastInterval.startDate);
    const lastKey = `${d.getFullYear()}-${d.getMonth()}`;

    if (!monthlyData[lastKey]) {
      monthlyData[lastKey] = {
        month: monthNames[d.getMonth()],
        categoryValues: {},
        count: 0,
      };
    }
  }

  const categoryDataMap = {};
  categories.forEach(category => {
    categoryDataMap[category] = [];
  });

  Object.keys(monthlyData)
    .sort((a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      return yearA - yearB || monthA - monthB;
    })
    .forEach(monthKey => {
      const monthData = monthlyData[monthKey];

      categories.forEach(category => {
        const values = monthData.categoryValues[category] || [];
        const avg =
          values.length > 0
            ? values.reduce((s, v) => s + v, 0) / values.length
            : 0;

        categoryDataMap[category].push(parseFloat((avg * 100).toFixed(2)));
      });
    });

  return categories.map(category => ({
    name: category,
    data: categoryDataMap[category] || [],
  }));
}

export function formatTimestamp(unixTimestamp) {
  if (!unixTimestamp) return '';

  const date = new Date(unixTimestamp * 1000);

  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function mapRanking(products, categories, data, netSentiment) {
  if (
    !Array.isArray(products) ||
    !Array.isArray(categories) ||
    !Array.isArray(data) ||
    !Array.isArray(netSentiment)
  ) {
    throw new Error('All inputs must be arrays');
  }

  const combined = products.map((product, index) => {
    const productData = data[index] || [];
    const dominantIndex = productData.indexOf(Math.max(...productData));
    const dominant = categories[dominantIndex] || null;

    return {
      product: product.name,
      netSentiment: netSentiment[index] || 0,
      dominant,
      originalIndex: index,
    };
  });

  combined.sort((a, b) => b.netSentiment - a.netSentiment);

  return combined.map((item, index) => ({
    ...item,
    rank: index + 1,
    recommended: item.originalIndex === 0,
    netSentiment: Math.round(item.netSentiment * 100), // percentage
  }));
}

export function mapRankingEmotion(products, categories, data, joyEmotion) {
  if (
    !Array.isArray(products) ||
    !Array.isArray(categories) ||
    !Array.isArray(data) ||
    !Array.isArray(joyEmotion)
  ) {
    throw new Error('All inputs must be arrays');
  }

  // Step 1: Map raw info (without rank)
  let mapped = products.map((product, index) => {
    const productData = data[index] || [];
    const dominantIndex = productData.indexOf(Math.max(...productData));
    const dominant = categories[dominantIndex] || null;

    const joyRating = Math.round((joyEmotion[index] || 0) * 100);

    return {
      product: product.name,
      joyRating,
      dominantEmotion: dominant,
      originalIndex: index,
    };
  });

  mapped.sort((a, b) => b.joyRating - a.joyRating);

  mapped = mapped.map((item, rankIndex) => ({
    product: item.product,
    rank: rankIndex + 1,
    joyRating: item.joyRating,
    recommended: item.originalIndex === 0,
    dominantEmotion: item.dominantEmotion,
  }));

  return mapped;
}

export function mapDistributionTableData(
  products,
  data,
  categories,
  startDate,
  endDate
) {
  if (
    !Array.isArray(products) ||
    !Array.isArray(data) ||
    !Array.isArray(categories)
  ) {
    throw new Error('All inputs must be arrays');
  }

  const startDateFormatted = formatTimestamp(startDate);
  const endDateFormatted = formatTimestamp(endDate);

  return products.map((product, index) => {
    const values = data[index] || [];

    const categoryValues = categories.reduce((acc, category, i) => {
      const key = category.toLowerCase().replace(/\s+/g, '');
      acc[key] = Math.round((values[i] || 0) * 100);
      return acc;
    }, {});

    return {
      product: product.name,
      recommended: index === 0,
      ...categoryValues,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
    };
  });
}

export function getFilteredPosts(posts, postIndices) {
  if (!Array.isArray(posts) || !Array.isArray(postIndices)) {
    return [];
  }

  return postIndices
    .map(index => posts[index])
    .filter(post => post !== undefined);
}

/**
 * Process matrix data preserving both product and category dimensions
 * Returns data structures for cross-filtering:
 * - byProduct: Each product has sentiment series
 * - byCategory: Each sentiment has product series
 */
export function processTimeIntervalMatrixDataForCrossFilter(
  byTimeInterval,
  products,
  categories,
  timeframe,
  fieldName
) {
  if (
    !byTimeInterval ||
    !Array.isArray(byTimeInterval) ||
    !products ||
    !Array.isArray(products) ||
    !categories ||
    !Array.isArray(categories)
  ) {
    return {
      byProduct: [],
      byCategory: [],
      categories: [],
    };
  }

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const processedIntervals = byTimeInterval
    .filter(i => i.startDate)
    .map(interval => {
      const start = new Date(interval.startDate);
      const matrix = interval[fieldName] || [];

      let dateLabel;
      if (timeframe === 'oneMonth' && interval.endDate) {
        const end = new Date(interval.endDate);
        const sm = monthNames[start.getMonth()];
        const sd = start.getDate();
        const em = monthNames[end.getMonth()];
        const ed = end.getDate();
        dateLabel =
          sm === em ? `${sm} ${sd} - ${ed}` : `${sm} ${sd} - ${em} ${ed}`;
      } else {
        dateLabel = monthNames[start.getMonth()];
      }

      return {
        dateLabel,
        startDate: interval.startDate,
        monthKey: `${start.getFullYear()}-${start.getMonth()}`,
        matrix: Array.isArray(matrix) ? matrix : [],
      };
    })
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const productDataMap = {};
  const categoryDataMap = {};
  const timeCategories = [];

  // Set up maps
  products.forEach(product => {
    const pName = product.name || product;
    productDataMap[pName] = {};
    categories.forEach(category => {
      productDataMap[pName][category] = [];
    });
  });

  categories.forEach(category => {
    categoryDataMap[category] = {};
    products.forEach(product => {
      const pName = product.name || product;
      categoryDataMap[category][pName] = [];
    });
  });

  if (timeframe === 'oneMonth') {
    processedIntervals.forEach(({ dateLabel, matrix }) => {
      timeCategories.push(dateLabel);

      // 2D matrix
      if (matrix.length > 0 && Array.isArray(matrix[0])) {
        products.forEach((product, pIndex) => {
          const pName = product.name || product;
          categories.forEach((category, cIndex) => {
            const value = matrix[pIndex]?.[cIndex] ?? 0;
            const normalized = parseFloat((value * 100).toFixed(2));
            productDataMap[pName][category].push(normalized);
            categoryDataMap[category][pName].push(normalized);
          });
        });
      }

      // 1D matrix (fallback)
      else if (matrix.length === categories.length) {
        products.forEach(product => {
          const pName = product.name || product;
          categories.forEach((category, cIndex) => {
            const value = matrix[cIndex] ?? 0;
            const normalized = parseFloat((value * 100).toFixed(2));
            productDataMap[pName][category].push(normalized);
            categoryDataMap[category][pName].push(normalized);
          });
        });
      }
    });
  } else {
    const monthlyData = {};

    processedIntervals.forEach(({ monthKey, matrix, startDate }) => {
      if (!monthlyData[monthKey]) {
        const d = new Date(startDate);
        monthlyData[monthKey] = {
          month: monthNames[d.getMonth()],
          intervals: [],
        };
      }

      if (matrix.length > 0 && Array.isArray(matrix[0])) {
        monthlyData[monthKey].intervals.push(matrix);
      }

      // Handle 1D matrices
      else if (matrix.length === categories.length) {
        const row = products.map(() => matrix); // copy row for each product
        monthlyData[monthKey].intervals.push(row);
      }
    });

    const last = processedIntervals[processedIntervals.length - 1];
    if (last) {
      const d = new Date(last.startDate);
      const lastKey = `${d.getFullYear()}-${d.getMonth()}`;
      if (!monthlyData[lastKey]) {
        monthlyData[lastKey] = {
          month: monthNames[d.getMonth()],
          intervals: [],
        };
      }
    }

    // Sort keys PROPERLY
    const sortedKeys = Object.keys(monthlyData).sort((a, b) => {
      const [yA, mA] = a.split('-').map(Number);
      const [yB, mB] = b.split('-').map(Number);
      return yA - yB || mA - mB;
    });

    // Aggregate per month
    sortedKeys.forEach(monthKey => {
      const monthData = monthlyData[monthKey];
      timeCategories.push(monthData.month);

      const aggregated = products.map(() => categories.map(() => 0));

      products.forEach((product, pIndex) => {
        categories.forEach((category, cIndex) => {
          const values = monthData.intervals.map(
            int => int[pIndex]?.[cIndex] ?? 0
          );

          const avg =
            values.length > 0
              ? values.reduce((s, v) => s + v, 0) / values.length
              : 0;

          aggregated[pIndex][cIndex] = avg;
        });
      });

      // Store normalized values
      products.forEach((product, pIndex) => {
        const pName = product.name || product;
        categories.forEach((category, cIndex) => {
          const normalized = parseFloat(
            (aggregated[pIndex][cIndex] * 100).toFixed(2)
          );
          productDataMap[pName][category].push(normalized);
          categoryDataMap[category][pName].push(normalized);
        });
      });
    });
  }

  const byProduct = products.map(product => {
    const pName = product.name || product;
    return {
      productName: pName,
      series: categories.map(category => ({
        name: category,
        data: productDataMap[pName][category],
      })),
    };
  });

  const byCategory = categories.map(category => {
    return {
      categoryName: category,
      series: products.map(product => {
        const pName = product.name || product;
        return {
          name: pName,
          data: categoryDataMap[category][pName],
        };
      }),
    };
  });

  return {
    byProduct,
    byCategory,
    categories: timeCategories,
  };
}

/**
 * Process a 3D matrix [products][dimension1][dimension2] into chart series.
 * Scalable for any dimensions (buying motivations/pain points, sentiment/emotions).
 * @param {Array} byTimeInterval - array of intervals with startDate, endDate, and matrix field
 * @param {Array} dimension1 - first category (e.g., buying motivations)
 * @param {Array} products - product names
 * @param {Array} dimension2 - second category (e.g., sentiments)
 * @param {String} timeframe - e.g., 'allTime', 'oneMonth'
 * @param {String} fieldName - field in interval (e.g., 'sentimentByBuyingMotivationsCategory')
 * @returns {Array} chart series array
 */
export const processTimeIntervalMatrixData3D = (
  byTimeInterval,
  dimension1,
  products,
  dimension2,
  timeframe,
  fieldName
) => {
  if (!byTimeInterval || !dimension1 || !products || !dimension2) return [];

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const now = new Date();

  // Filter intervals by timeframe
  const filteredIntervals = byTimeInterval.filter(interval => {
    if (!interval.startDate) return false;
    const start = new Date(interval.startDate);

    const xMonthsAgo = months => {
      const d = new Date(now);
      d.setMonth(d.getMonth() - months);
      return d;
    };

    if (timeframe === 'oneMonth') return start >= xMonthsAgo(1);
    if (timeframe === 'threeMonths') return start >= xMonthsAgo(3);
    if (timeframe === 'sixMonths') return start >= xMonthsAgo(6);

    return true; // allTime
  });

  // Sort intervals chronologically
  filteredIntervals.sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate)
  );

  // Determine if year should be included
  const years = new Set(
    filteredIntervals.map(i => new Date(i.startDate).getFullYear())
  );
  const includeYear = timeframe === 'allTime' || years.size > 1;

  const monthlyData = {};
  const seriesMap = {};

  // Init series
  products.forEach(p => {
    dimension1.forEach(d1 => {
      dimension2.forEach(d2 => {
        seriesMap[`${p} - ${d1} - ${d2}`] = [];
      });
    });
  });

  if (timeframe === 'oneMonth') {
    // oneMonth → push interval directly
    filteredIntervals.forEach(interval => {
      const matrix = interval[fieldName] || [];

      products.forEach((p, i) => {
        dimension1.forEach((d1, j) => {
          dimension2.forEach((d2, k) => {
            const val = matrix[i]?.[j]?.[k] ?? 0;
            seriesMap[`${p} - ${d1} - ${d2}`].push(
              parseFloat((val * 100).toFixed(2))
            );
          });
        });
      });
    });
  } else {
    // Group by month
    filteredIntervals.forEach(interval => {
      const start = new Date(interval.startDate);
      const year = start.getFullYear();
      const month = start.getMonth();
      const monthKey = `${year}-${month}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          monthLabel: includeYear
            ? `${monthNames[month]} ${year}`
            : monthNames[month],
          intervals: [],
        };
      }

      monthlyData[monthKey].intervals.push(interval);
    });

    // Guarantee last month exists
    if (filteredIntervals.length > 0) {
      const last = new Date(
        filteredIntervals[filteredIntervals.length - 1].startDate
      );
      const lastKey = `${last.getFullYear()}-${last.getMonth()}`;
      if (!monthlyData[lastKey]) {
        monthlyData[lastKey] = {
          monthLabel: includeYear
            ? `${monthNames[last.getMonth()]} ${last.getFullYear()}`
            : monthNames[last.getMonth()],
          intervals: [],
        };
      }
    }

    // NUMERIC sort by year then month
    Object.keys(monthlyData)
      .sort((a, b) => {
        const [yA, mA] = a.split('-').map(Number);
        const [yB, mB] = b.split('-').map(Number);
        return yA - yB || mA - mB;
      })
      .forEach(monthKey => {
        const { intervals } = monthlyData[monthKey];

        products.forEach((p, i) => {
          dimension1.forEach((d1, j) => {
            dimension2.forEach((d2, k) => {
              const values = intervals.map(interval => {
                const matrix = interval[fieldName] || [];
                return matrix[i]?.[j]?.[k] ?? 0;
              });

              const avg =
                values.length > 0
                  ? values.reduce((acc, v) => acc + v, 0) / values.length
                  : 0;

              seriesMap[`${p} - ${d1} - ${d2}`].push(
                parseFloat((avg * 100).toFixed(2))
              );
            });
          });
        });
      });
  }

  // Convert to series array
  return Object.entries(seriesMap).map(([name, data]) => ({ name, data }));
};

/**
 * Process 3D matrix into cross-filter friendly structure.
 * Allows filtering by product, dimension1, or dimension2.
 * @returns {Object} { productDataMap, dimension1DataMap, dimension2DataMap, timeCategories }
 */
export const processTimeIntervalMatrixDataForCrossFilter3D = (
  byTimeInterval,
  dimension2,
  products,
  dimension1,
  timeframe,
  fieldName
) => {
  if (!byTimeInterval || !dimension1 || !products || !dimension2) return null;

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const filteredIntervals = byTimeInterval
    .filter(interval => interval.startDate)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const years = new Set(
    filteredIntervals.map(i => new Date(i.startDate).getFullYear())
  );
  const includeYear = timeframe === 'allTime' || years.size > 1;

  const timeCategories = [];
  const productDataMap = {};
  const dimension1DataMap = {};
  const dimension2DataMap = {};

  products.forEach(p => {
    productDataMap[p] = {};
    dimension1.forEach(d1 => {
      productDataMap[p][d1] = {};
      dimension2.forEach(d2 => (productDataMap[p][d1][d2] = []));
    });
  });

  dimension1.forEach(d1 => {
    dimension1DataMap[d1] = {};
    products.forEach(p => {
      dimension1DataMap[d1][p] = {};
      dimension2.forEach(d2 => (dimension1DataMap[d1][p][d2] = []));
    });
  });

  dimension2.forEach(d2 => {
    dimension2DataMap[d2] = {};
    products.forEach(p => {
      dimension2DataMap[d2][p] = {};
      dimension1.forEach(d1 => (dimension2DataMap[d2][p][d1] = []));
    });
  });

  if (timeframe === 'oneMonth') {
    filteredIntervals.forEach(interval => {
      const start = new Date(interval.startDate);
      const dateLabel = interval.endDate
        ? `${monthNames[start.getMonth()]} ${start.getDate()} - ${
            monthNames[new Date(interval.endDate).getMonth()]
          } ${new Date(interval.endDate).getDate()}`
        : `${monthNames[start.getMonth()]} ${start.getDate()}`;
      timeCategories.push(dateLabel);

      const matrix = interval[fieldName] || [];
      products.forEach((p, i) => {
        dimension1.forEach((d1, j) => {
          dimension2.forEach((d2, k) => {
            const val = matrix[i]?.[j]?.[k] ?? 0;
            const normalized = parseFloat((val * 100).toFixed(2));
            productDataMap[p][d1][d2].push(normalized);
            dimension1DataMap[d1][p][d2].push(normalized);
            dimension2DataMap[d2][p][d1].push(normalized);
          });
        });
      });
    });
  } else {
    const monthlyData = {};

    filteredIntervals.forEach(interval => {
      const start = new Date(interval.startDate);
      const monthKey = `${start.getFullYear()}-${start.getMonth()}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          monthLabel: includeYear
            ? `${monthNames[start.getMonth()]} ${start.getFullYear()}`
            : monthNames[start.getMonth()],
          intervals: [],
        };
      }
      monthlyData[monthKey].intervals.push(interval);
    });

    if (filteredIntervals.length > 0) {
      const last = new Date(
        filteredIntervals[filteredIntervals.length - 1].startDate
      );
      const lastKey = `${last.getFullYear()}-${last.getMonth()}`;
      if (!monthlyData[lastKey]) {
        monthlyData[lastKey] = {
          monthLabel: includeYear
            ? `${monthNames[last.getMonth()]} ${last.getFullYear()}`
            : monthNames[last.getMonth()],
          intervals: [],
        };
      }
    }

    Object.keys(monthlyData)
      .sort((a, b) => {
        const [yA, mA] = a.split('-').map(Number);
        const [yB, mB] = b.split('-').map(Number);
        return yA - yB || mA - mB;
      })
      .forEach(monthKey => {
        const { monthLabel, intervals } = monthlyData[monthKey];
        timeCategories.push(monthLabel);

        products.forEach((p, i) => {
          dimension1.forEach((d1, j) => {
            dimension2.forEach((d2, k) => {
              const values = intervals
                .map(interval => {
                  const matrix = interval[fieldName] || [];
                  return matrix[i]?.[j]?.[k] ?? 0;
                })
                .filter(v => v !== null && v !== undefined);

              const avg =
                values.length > 0
                  ? values.reduce((sum, v) => sum + v, 0) / values.length
                  : 0;
              const normalized = parseFloat((avg * 100).toFixed(2));

              productDataMap[p][d1][d2].push(normalized);
              dimension1DataMap[d1][p][d2].push(normalized);
              dimension2DataMap[d2][p][d1].push(normalized);
            });
          });
        });
      });
  }

  return {
    productDataMap,
    dimension1DataMap,
    dimension2DataMap,
    timeCategories,
  };
};

export function getPlatformLabel(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '';

  const capitalizeFirst = str => {
    if (!str || str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  if (arr.length === 1) {
    return `<b>${capitalizeFirst(arr[0])}</b>`;
  }
  const allButLast = arr
    .slice(0, -1)
    .map(v => `<b>${capitalizeFirst(v)}</b>`)
    .join(', ');
  const last = `<b>${capitalizeFirst(arr[arr.length - 1])}</b>`;
  return `${allButLast} and ${last}`;
}

export function getDateRangeFromTimeframe(timeframe) {
  const now = new Date();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  let startDate;
  let endDate;

  if (timeframe === 'oneMonth') {
    startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - 1);
    endDate = new Date(now);
  } else if (timeframe === 'threeMonths') {
    startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - 3);
    endDate = new Date(now);
  } else if (timeframe === 'sixMonths') {
    startDate = new Date(now);
    startDate.setMonth(startDate.getMonth() - 6);
    endDate = new Date(now);
  } else {
    startDate = new Date(now);
    startDate.setFullYear(startDate.getFullYear() - 1);
    endDate = new Date(now);
  }

  const formatDate = date => {
    return `${
      monthNames[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()}`;
  };

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}

export function getTotalPostsAnalyzed(data, period, productName, type) {
  if (!data || !data[period]) return 0;

  // 1. Normalize to array (strings or array)
  let products = [];

  if (Array.isArray(productName)) {
    // if it's [], use ALL products in that period
    products = productName.length > 0 ? productName : Object.keys(data[period]);
  } else {
    // single string
    products = [productName];
  }

  return products.reduce((total, name) => {
    const product = data[period][name];
    if (!product) return total;

    switch (type) {
      case 'all':
        return total + (product.all || 0);
      case 'mb': // buying motivations
        return total + (product.withBuyingMotivations || 0);
      case 'pp': // pain points
        return total + (product.withPainPoints || 0);
      default:
        return total;
    }
  }, 0);
}

export function sumPostIndices(obj, seen = new Set(), productNames = null) {
  if (!obj || typeof obj !== 'object') return 0;

  // If productNames is provided, filter by those products at the top level
  const keysToProcess =
    productNames && Array.isArray(productNames) && productNames.length > 0
      ? Object.keys(obj).filter(key => productNames.includes(key))
      : Object.keys(obj);

  keysToProcess.forEach(key => {
    const value = obj[key];

    if (Array.isArray(value?.postIndices)) {
      value.postIndices.forEach(i => seen.add(i));
    }

    if (value && typeof value === 'object') {
      // For nested objects, don't pass productNames to avoid double filtering
      sumPostIndices(value, seen, null);
    }
  });

  return seen.size;
}

export function getHighlightsRanking(categories, items, matrix) {
  const results = {};

  // Validate inputs
  if (
    !Array.isArray(categories) ||
    !Array.isArray(items) ||
    !Array.isArray(matrix)
  ) {
    return results;
  }

  categories.forEach((categoryName, categoryIndex) => {
    const ranking = items
      .map((item, itemIndex) => {
        // Safely access matrix values with fallback to 0
        const row = matrix[itemIndex];
        const score =
          row && Array.isArray(row) && row[categoryIndex] !== undefined
            ? row[categoryIndex]
            : 0;

        return {
          item: item.name,
          logo: item.logo,
          score,
        };
      })
      .sort((a, b) => b.score - a.score);

    results[categoryName] = ranking;
  });

  return results;
}

export function getTopCategoryRanking(matrix, categories, labels, index = 0) {
  // Validate matrix
  if (!Array.isArray(matrix) || !Array.isArray(matrix[index])) return {};

  const selected = matrix[index] || [];

  // Ensure each row is an array
  const safeRows = selected.map(row => (Array.isArray(row) ? row : []));

  // Determine max number of emotions in any category
  const maxEmotionCount = safeRows.reduce(
    (max, row) => Math.max(max, row.length),
    0
  );

  if (maxEmotionCount === 0) return {};

  // Transpose matrix safely
  const transposed = Array.from(
    { length: maxEmotionCount },
    (_, emotionIndex) =>
      safeRows.map(
        categoryScores => Number(categoryScores?.[emotionIndex]) || 0
      )
  );

  const result = {};

  transposed.forEach((scoresByCategory, emotionIndex) => {
    const emotionLabel = labels?.[emotionIndex] || `Label ${emotionIndex}`;

    const ranking = scoresByCategory.map((score, catIndex) => ({
      category: categories?.[catIndex] || `Category ${catIndex}`,
      score: Number(score) || 0,
    }));

    ranking.sort((a, b) => b.score - a.score);

    result[emotionLabel] = ranking;
  });

  return result;
}

export function getTopCategoryRanking2(matrix, categories, labels) {
  if (!Array.isArray(matrix) || !matrix.length) return [];
  if (!Array.isArray(categories) || !Array.isArray(labels)) return [];

  // matrix shape: [categoryIndex][emotionIndex]
  // Get scores for emotionIndex 0 only
  const scoresByCategory = matrix.map(row => row[0] || 0);

  // Create ranking with categories ordered by highest score
  const ranking = scoresByCategory
    .map((score, catIndex) => ({
      category: categories[catIndex],
      score,
    }))
    .sort((a, b) => b.score - a.score);

  return ranking;
}
